export interface ProductItem {
    product_id: string;
    quantity?: number;
}

/** VN e-invoice / VAT (MST) details — sent with payment when currency is VND */
export interface TaxInfo {
    tax_number: string;
    company_name: string;
    buyer_name?: string;
    company_address: string;
    invoice_email: string;
}

/**
 * Optional values to pre-fill the VN tax form from {@link PaymentConfig}
 * (`email`, `name`) and optional `metadata` keys: `company_name`, `company`,
 * `company_address`, `address`, `tax_number`, `mst`, `maso_thue`.
 */
export type TaxFormPrefill = Partial<
    Pick<TaxInfo, 'tax_number' | 'company_name' | 'buyer_name' | 'company_address' | 'invoice_email'>
>;

/** Derive tax form defaults from `email`, `name`, and optional `metadata` (see {@link TaxFormPrefill}). */
export function buildTaxFormPrefillFromConfig(config: PaymentConfig): TaxFormPrefill {
    const meta = config.metadata || {};
    const out: TaxFormPrefill = {};
    const email = (config.email || '').trim();
    if (email) {
        out.invoice_email = email;
    }
    const name = (config.name || '').trim();
    if (name) {
        out.buyer_name = name;
    }
    const company = String(meta.company_name || meta.company || '').trim();
    if (company) {
        out.company_name = company;
    }
    const addr = String(meta.company_address || meta.address || '').trim();
    if (addr) {
        out.company_address = addr;
    }
    const mst = String(meta.tax_number || meta.mst || meta.maso_thue || '').trim();
    if (mst) {
        out.tax_number = mst;
    }
    if (config.tax_info) {
        const ti = config.tax_info;
        if (ti.invoice_email) {
            out.invoice_email = ti.invoice_email;
        }
        if (ti.buyer_name) {
            out.buyer_name = ti.buyer_name;
        }
        if (ti.company_name) {
            out.company_name = ti.company_name;
        }
        if (ti.company_address) {
            out.company_address = ti.company_address;
        }
        if (ti.tax_number) {
            out.tax_number = ti.tax_number;
        }
    }
    return out;
}

export interface PaymentConfig {
    environment: 'production' | 'staging' | 'development';
    baseUrl?: string;
    product_id?: string;
    // Multi-product voucher: use either product_id or products (not both). Only supported with grant_option='voucher'.
    products?: ProductItem[];
    order_id?: string;
    email?: string;
    phone?: string;
    payment_type?: 'subscription' | 'onetime';
    user_id?: string | null;
    access_token?: string | null;
    source_url?: string;
    site_code?: string;
    promotion_code?: string;
    promotion_token?: string;
    upgrade?: boolean;
    grant_option?: 'auto' | 'voucher';
    name?: string;
    /**
     * Tracking/CRM metadata — forwarded to Zoho Lead and stored in Stripe metadata.
     * Applies to all payment types (subscription, onetime, dynamic).
     *
     * @example
     * {
     *   lead_source: 'VNCO_SHOPEEPAY',
     *   utm_source: 'GoogleSEM_', utm_medium: 'GA_ELSAVNUSD_Search_PM_BrandELSA',
     *   utm_campaign: 'elsa', utm_content: 'học tiếng anh elsa', utm_term: 'b',
     *   campaign_id: '18590798047', adset_id: '137561262890', ads_id: '628053185960',
     *   g_keyword: 'học tiếng anh elsa', g_matchtype: 'BROAD',
     *   gclid: 'CjwKCAjwtTNBhB0EiwAuswY...',
     *   fbp: 'fb.1.1773520983144.5551919102847896',
     *   country: 'VN',
     *   // Optional — pre-fill VN tax / e-invoice form when currency is VND:
     *   company_name: 'Cong ty ABC',
     *   company_address: '123 Nguyen Hue',
     *   tax_number: '0123456789', // or mst / maso_thue
     * }
     */
    metadata?: Record<string, any>;
    // Dynamic product fields: required when product_id is 'dynamic'
    amount?: number;
    currency?: string;
    // Custom payment methods configuration
    customPaymentMethods?: CustomPaymentMethod[];
    defaultPaymentMethod?: 'stripe_js' | 'paypal' | 'payoo' | 'momo' | 'shopee' | 'fundiin' | 'my2c2p';
    showStripeElementByDefault?: boolean;
    locale?: 'en' | 'vi';
    /** Set on submit when user opts in to VAT invoice (VND); synced via checkout-tax-info before pay */
    tax_info?: TaxInfo;
}

export interface PaymentRequest {
    amount: number;
    currency: string;
    orderId: string;
    description?: string;
    customerInfo?: CustomerInfo;
    metadata?: Record<string, string | number | boolean>;
}

export interface CustomerInfo {
    name: string;
    email: string;
    phone?: string;
    address?: Address;
}

export interface Address {
    street: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
}

export interface PaymentResponse {
    success: boolean;
    transactionId?: string;
    paymentUrl?: string;
    error?: string;
    data?: Record<string, unknown>;
}

export interface PaymentOptions {
    redirectUrl?: string;
    cancelUrl?: string;
    webhookUrl?: string;
    theme?: 'light' | 'dark';
    locale?: string;
}

export type PaymentMethod =
    | 'card'
    | 'bank_transfer'
    | 'digital_wallet'
    | 'qr_code';

export interface PaymentMethodConfig {
    enabled: PaymentMethod[];
    preferred?: PaymentMethod;
}

export interface SDKEventData {
    type:
        | 'payment_success'
        | 'payment_error'
        | 'payment_cancel'
        | 'payment_pending';
    data?: Record<string, unknown>;
}

export type EventCallback = (data: SDKEventData) => void;

// Custom payment method definition
export interface CustomPaymentMethod {
    id: 'stripe_js' | 'paypal' | 'payoo' | 'momo' | 'shopee' | 'fundiin' | 'my2c2p';
    label: string;
    description?: string;
    icon?: string;
    enabled: boolean;
    requiresStripe: boolean;
}

// Payment method selection event
export interface PaymentMethodChangeEvent {
    previousMethod: string;
    currentMethod: 'stripe_js' | 'paypal' | 'payoo' | 'momo' | 'shopee' | 'fundiin' | 'my2c2p';
    requiresStripe: boolean;
}

// Custom payment response (for PayPal/Payoo)
export interface CustomPaymentResponse {
    success: boolean;
    paymentMethod: 'paypal' | 'payoo' | 'momo' | 'shopee' | 'fundiin' | 'my2c2p';
    redirectUrl?: string;
    transactionId?: string;
    orderId?: string;
    error?: string;
    data?: Record<string, unknown>;
}

// Default custom payment methods
export const DEFAULT_CUSTOM_PAYMENT_METHODS: CustomPaymentMethod[] = [
    {
        id: 'stripe_js',
        label: 'Payment methods support by Stripe & Links',
        description: 'Credit card, Google Pay, Apple Pay, Samsung Pay, and more',
        icon: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="25" viewBox="0 0 60 25" aria-label="Stripe logo"><path fill="#635bff" fill-rule="evenodd" d="M59.6444 14.2813h-8.062c.1843 1.9296 1.5983 2.5476 3.2032 2.5476 1.6352 0 2.9534-.3656 4.0453-.9506v3.3179c-1.1186.7115-2.5964 1.1068-4.5645 1.1068-4.011 0-6.8218-2.5122-6.8218-7.4783 0-4.19441 2.3837-7.52509 6.3017-7.52509 3.912 0 5.9537 3.28038 5.9537 7.49819 0 .3982-.0372 1.261-.0556 1.4835Zm-5.9241-5.62407c-1.0294 0-2.1739.72812-2.1739 2.58387h4.2573c0-1.85362-1.0721-2.58387-2.0834-2.58387ZM40.9547 20.303c-1.4411 0-2.322-.6087-2.9133-1.0417l-.0088 4.6271-4.1181.8755-.0014-19.19053h3.7543l.0864 1.01784c.6035-.52914 1.6114-1.29157 3.2256-1.29162 2.8925 0 5.6162 2.6052 5.6162 7.39971 0 5.2327-2.6948 7.6037-5.6409 7.6037Zm-.959-11.35573c-.9453 0-1.5376.34559-1.9669.81586l.0245 6.11967c.3997.433.9763.7813 1.9424.7813 1.5231 0 2.5437-1.6575 2.5437-3.8745 0-2.1544-1.037-3.84233-2.5437-3.84233Zm-11.7602-3.3739h4.1341V20.0088h-4.1341V5.57337Zm0-4.694699L32.3696 0v3.35821l-4.1341.87868V.878671ZM23.9198 10.2223v9.7861h-4.1156V5.57296h3.6867l.1317 1.21751c1.0035-1.7722 3.0722-1.41321 3.6209-1.21594v3.78524c-.5242-.16908-2.2894-.42779-3.3237.86253Zm-8.5525 4.7221c0 2.4275 2.5988 1.6719 3.1263 1.4609v3.3522c-.5492.3013-1.5437.5458-2.8901.5458-2.4441 0-4.2773-1.7999-4.2773-4.2379l.0173-13.17658 4.0206-.85464.0032 3.5395h3.1278V9.0857h-3.1278v5.8588-.0001Zm-4.9069.7026c0 2.9645-2.31051 4.6562-5.73464 4.6562-1.41958 0-2.92289-.2761-4.453935-.9347v-3.9319c1.382085.7516 3.093705 1.315 4.457755 1.315.91864 0 1.53106-.2459 1.53106-1.0069C6.26064 13.7786 0 14.5192 0 9.95995 0 7.04457 2.27622 5.2998 5.61655 5.2998c1.36404 0 2.72806.20934 4.09208.75351V9.9317c-1.25265-.67618-2.84332-1.05979-4.09588-1.05979-.86296 0-1.44753.24965-1.44753.8924.0001 1.85329 6.29518.97249 6.29518 5.88279v-.0001Z" clip-rule="evenodd"></path></svg>',
        enabled: true,
        requiresStripe: true
    },
    {
        id: 'paypal',
        label: 'PayPal',
        description: 'Pay with your PayPal account',
        icon: '<img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" alt="PayPal" style="height: 24px; width: 80px; object-fit: contain;" />',
        enabled: true,
        requiresStripe: false
    },
    {
        id: 'payoo',
        label: 'Payoo',
        description: 'Wallet, Bank Transfer, ATM (Domestic cards) & QR Pay in Viet Nam',
        icon: '<img src="https://latestlogo.com/wp-content/uploads/2024/01/payoo-logo.png" alt="Payoo" style="height: 24px; width: 80px; object-fit: contain;" />',
        enabled: true,
        requiresStripe: false
    },
    {
        id: 'momo',
        label: 'MoMo',
        description: 'Pay with MoMo',
        icon: '<img src="https://content-media.elsanow.co/_extras_/momo-logo.png" alt="MoMo" style="height: 48px; width: 80px; object-fit: contain;" />',
        enabled: true,
        requiresStripe: false
    },
    {
        id: 'shopee',
        label: 'ShopeePay',
        description: 'Pay with ShopeePay',
        icon: '<img src="https://content-media.elsanow.co/_extras_/shopeepay-logo.jpg" alt="Shopee" style="height: 24px; width: 80px; object-fit: contain;" />',
        enabled: true,
        requiresStripe: false
    },
    {
        id: 'fundiin',
        label: 'Fundiin',
        description: 'Pay later with Fundiin',
        icon: '<img src="https://content-media.elsanow.co/_extras_/fundiin-logo.png" alt="Fundiin" style="height: 44px; width: 80px; object-fit: contain;" />',
        enabled: true,
        requiresStripe: false
    },
    {
        id: 'my2c2p',
        label: '2C2P',
        description: 'Pay via 2C2P payment gateway',
        icon: '<img src="https://content-media.elsanow.co/_extras_/logo_2c2p.png" alt="2C2P" style="width: 80px; object-fit: contain;" />',
        enabled: true,
        requiresStripe: false
    }
];
